import { ErrorComponent } from '@refinedev/antd';
import { useCustom, useTranslate } from '@refinedev/core';
import { Layout, Menu, Space, Spin, Tag, Typography, theme } from 'antd';
import { Link, Outlet, useLocation } from 'react-router-dom';

/**
 * E33 · Backoffice — Shell (board cc-backoffice, "Backoffice — Access & Shell (portal, E33)"). The
 * staff console inside the portal build: its own left menu and the staff badge, never linked from the
 * therapist navigation. The gate is the SERVER's `backofficeShell` (the BackofficeMembers fold): a
 * non-member — a therapist who typed the address — sees the portal's ordinary "page not found", no
 * hint that anything is here. Sign-in is the portal's own; the backoffice adds a gate, not a login.
 */
export type BackofficeShell = { isMember: boolean; sections: string[] };

export const useBackofficeShell = () =>
  useCustom<BackofficeShell>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'backofficeShell', fields: ['isMember', 'sections'] },
  });

export const BackofficeLayout = () => {
  const translate = useTranslate();
  // the portal's own sidebar look (the Serene theme styles Layout.Sider sage with light menu text)
  const { token } = theme.useToken();
  const onSider = token.colorTextLightSolid;
  const { pathname } = useLocation();
  const { data, isLoading, isError } = useBackofficeShell();
  const shell = data?.data;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin />
      </div>
    );
  }
  if (isError || !shell?.isMember) {
    return <ErrorComponent />;
  }

  const selected = pathname.startsWith('/backoffice/erasure/log')
    ? 'erasureLog'
    : pathname.startsWith('/backoffice/erasure')
    ? 'erasure'
    : pathname.startsWith('/backoffice/worksheets')
    ? 'worksheets'
    : pathname.startsWith('/backoffice/diary-card-templates')
    ? 'diaryCardTemplates'
    : pathname.startsWith('/backoffice/instruments') // E35
    ? 'instruments'
    : pathname.startsWith('/backoffice/activity-library') // E41
    ? 'activityLibrary'
    : pathname.startsWith('/backoffice/values-decks')
    ? 'valuesDecks'
    : pathname.startsWith('/backoffice/courses/review')
    ? 'courseReview'
    : pathname.startsWith('/backoffice/courses')
    ? 'courses'
    : pathname.startsWith('/backoffice/practices')
    ? 'practices'
    : 'members';

  return (
    <Layout style={{ minHeight: '100vh' }} data-testid="backoffice-shell">
      <Layout.Sider width={232} breakpoint="lg" collapsedWidth={0}>
        <Space
          direction="vertical"
          size={4}
          style={{ padding: '20px 20px 12px', width: '100%' }}
        >
          <Typography.Title level={4} style={{ margin: 0, color: onSider }}>
            {translate('backoffice.title')}
          </Typography.Title>
          <Tag color="volcano" data-testid="backoffice-staff-badge">
            {translate('backoffice.staffBadge')}
          </Tag>
        </Space>
        <Menu
          mode="inline"
          selectedKeys={[selected]}
          items={[
            {
              key: 'members',
              label: (
                <Link
                  to="/backoffice/members"
                  data-testid="backoffice-menu-members"
                >
                  {translate('backoffice.menu.members')}
                </Link>
              ),
            },
            {
              key: 'erasure',
              label: (
                <Link
                  to="/backoffice/erasure"
                  data-testid="backoffice-menu-erasure"
                >
                  {translate('backoffice.menu.erasure')}
                </Link>
              ),
            },
            {
              key: 'erasureLog',
              label: (
                <Link
                  to="/backoffice/erasure/log"
                  data-testid="backoffice-menu-erasure-log"
                >
                  {translate('backoffice.menu.erasureLog')}
                </Link>
              ),
            },
            // E34 block 2a — the CopingCard worksheet library (PublishWorksheetTemplate)
            {
              key: 'worksheets',
              label: (
                <Link
                  to="/backoffice/worksheets"
                  data-testid="backoffice-menu-worksheets"
                >
                  {translate('backoffice.menu.worksheets')}
                </Link>
              ),
            },
            // E37 — the diary card template catalogue (PublishDiaryCardTemplate)
            {
              key: 'diaryCardTemplates',
              label: (
                <Link
                  to="/backoffice/diary-card-templates"
                  data-testid="backoffice-menu-diary-card-templates"
                >
                  {translate('backoffice.menu.diaryCardTemplates')}
                </Link>
              ),
            },
            // E35 — the instrument definitions (PublishInstrument)
            {
              key: 'instruments',
              label: (
                <Link to="/backoffice/instruments" data-testid="backoffice-menu-instruments">
                  {translate('backoffice.menu.instruments')}
                </Link>
              ),
            },
            // E41 — the behavioral-activation activity library (PublishActivityLibrary)
            {
              key: 'activityLibrary',
              label: (
                <Link to="/backoffice/activity-library" data-testid="backoffice-menu-activity-library">
                  {translate('backoffice.menu.activityLibrary')}
                </Link>
              ),
            },
            // E36 — the values card-sort decks (PublishValuesDeck)
            {
              key: 'valuesDecks',
              label: (
                <Link to="/backoffice/values-decks" data-testid="backoffice-menu-values-decks">
                  {translate('backoffice.menu.valuesDecks')}
                </Link>
              ),
            },
            // E34 block 5a — the course library's access rules, the course review, the practices' capabilities
            {
              key: 'courses',
              label: (
                <Link to="/backoffice/courses" data-testid="backoffice-menu-courses">
                  {translate('backoffice.menu.courses')}
                </Link>
              ),
            },
            {
              key: 'courseReview',
              label: (
                <Link to="/backoffice/courses/review" data-testid="backoffice-menu-course-review">
                  {translate('backoffice.menu.courseReview')}
                </Link>
              ),
            },
            {
              key: 'practices',
              label: (
                <Link to="/backoffice/practices" data-testid="backoffice-menu-practices">
                  {translate('backoffice.menu.practices')}
                </Link>
              ),
            },
          ]}
        />
        <div style={{ padding: 20 }}>
          <Link to="/" style={{ color: onSider }}>
            {translate('backoffice.backToPortal')}
          </Link>
        </div>
      </Layout.Sider>
      {/* minWidth 0: a flex child may shrink, so a wide table scrolls in its card, never the page */}
      <Layout.Content style={{ padding: 24, maxWidth: 1200, minWidth: 0 }}>
        <Outlet />
      </Layout.Content>
    </Layout>
  );
};
